// StrategyContext.tsx
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { Strategy } from '../types/strategy';
import { api } from '../utils/api';
import { ApiResponse, ApiError } from '../types/api';
import { useDate } from './DateContext';
import { useTimezone } from './TimezoneContext';
import { useAutoRecalculate } from '../hooks/useAutoRecalculate';

export type StrategyState = 'queued' | 'running' | 'succeeded' | 'failed';

export type StrategyResultMap = Record<string, Strategy> | null;

export interface StrategyStatusPayload {
  jobKey: string;
  code: number;
  message: string;
  state: StrategyState;
  result?: StrategyResultMap;
}

interface StrategyJobResponse {
  jobKey: string;
  status: StrategyStatusPayload;
}

interface StrategyContextType {
  selectedStrategy: Strategy | null;
  setSelectedStrategy: (strategy: Strategy | null) => void;
  // Bumped whenever something that affects fuel loads changes (treatments,
  // vegetation edits, project config). With the auto-update preference on
  // (the default) this triggers a debounced recalculation; with it off it
  // only marks the result out-of-date.
  strategyRefreshToken: number;
  refreshStrategy: () => void;
  // Job lifecycle (formerly the per-component useStrategy hook). Centralised
  // here so the Recalculate button, the layers, the footer and the snackbar
  // all share one source of truth.
  strategy: { data: StrategyResultMap } | null;
  status: StrategyStatusPayload | null;
  isLoading: boolean;
  error: ApiError | undefined;
  // True when the current selection/inputs differ from what was last
  // calculated (including before the first calculation).
  isOutOfDate: boolean;
  // Submit (or re-submit, for retry) a fuel-load calculation for the current
  // strategy/date/inputs.
  recalculate: () => void;
}

const noop = () => {};

const StrategyContext = createContext<StrategyContextType>({
  selectedStrategy: null,
  setSelectedStrategy: noop,
  strategyRefreshToken: 0,
  refreshStrategy: noop,
  strategy: null,
  status: null,
  isLoading: false,
  error: undefined,
  isOutOfDate: false,
  recalculate: noop,
});

export const StrategyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { projectId } = useParams<{ projectId: string }>();
  const { selectedDate } = useDate();
  const { selectedTimezone } = useTimezone();

  const [selectedStrategy, setSelectedStrategy] = useState<Strategy | null>(null);
  const [strategyRefreshToken, setStrategyRefreshToken] = useState(0);
  // The value of strategyRefreshToken captured at the last dispatch. The result
  // is "out of date" when edits (treatments / vegetation / config) have bumped
  // the token since then. Changing the strategy or date auto-recalculates and
  // does NOT mark the result out of date.
  const [appliedToken, setAppliedToken] = useState(0);
  // Bumped on every dispatch (auto on strategy/date change, or manual via the
  // Recalculate button) to drive the submit query and allow retries.
  const [recalcNonce, setRecalcNonce] = useState(0);
  const [jobKey, setJobKey] = useState<string | null>(null);

  const date = selectedDate?.tz(selectedTimezone).format('YYYY-MM-DD');

  // Identity for automatic recalculation: changing the selected strategy or date
  // recalculates automatically (these are "navigation" choices).
  const navKey =
    selectedStrategy && date ? `${projectId}__${selectedStrategy.id}__${date}` : null;

  const refreshStrategy = useCallback(() => {
    setStrategyRefreshToken((token) => token + 1);
  }, []);

  const dispatchCalculation = useCallback(() => {
    setAppliedToken(strategyRefreshToken);
    setRecalcNonce((nonce) => nonce + 1);
  }, [strategyRefreshToken]);

  const recalculate = useCallback(() => {
    dispatchCalculation();
  }, [dispatchCalculation]);

  // Auto-fetch whenever the strategy or date changes (but not on edit-driven
  // token changes, which only mark the result out of date).
  useEffect(() => {
    if (!navKey) return;
    dispatchCalculation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navKey]);

  const autoRecalculate = useAutoRecalculate();

  // Auto-update: edit-driven token bumps trigger a recalculation, debounced so
  // bulk edits collapse into one job (each bump recreates dispatchCalculation,
  // so the cleanup resets the timer). After the dispatch the tokens match and
  // the effect early-returns, so there is no loop. Switching the preference on
  // while out of date recalculates immediately. When the preference is off the
  // effect is inert and edits only mark the result out of date.
  useEffect(() => {
    if (!autoRecalculate || !navKey) return;
    if (strategyRefreshToken === appliedToken) return;
    const timer = setTimeout(dispatchCalculation, 400);
    return () => clearTimeout(timer);
  }, [autoRecalculate, navKey, strategyRefreshToken, appliedToken, dispatchCalculation]);

  // Each dispatch starts a new job, so drop the previously polled jobKey.
  useEffect(() => {
    setJobKey(null);
  }, [recalcNonce]);

  // 1. Submit the job once per dispatch (a nonce bump with no active jobKey).
  const submitEnabled = !!selectedStrategy && !!date && recalcNonce > 0 && !jobKey;

  const submitQuery = useQuery<ApiResponse<StrategyJobResponse>, ApiError>({
    queryKey: ['strategyJob', selectedStrategy?.id, date, recalcNonce],
    enabled: submitEnabled,
    refetchOnWindowFocus: false,
    queryFn: () => {
      if (!selectedStrategy || !date) {
        return Promise.reject(new Error('Strategy and date are required'));
      }

      const query = `/api/projects/${projectId}/strategyForYear?queryDate=${date}&mitigationStrategyId=${selectedStrategy.id}`;
      return api.request<ApiResponse<StrategyJobResponse>>(query);
    },
  });

  // 2. Poll status while a jobKey is present and not complete.
  const statusQuery = useQuery<ApiResponse<StrategyStatusPayload>, ApiError>({
    queryKey: ['strategyStatus', jobKey],
    enabled: !!jobKey,
    refetchOnWindowFocus: false,
    queryFn: () => {
      if (!jobKey) {
        return Promise.reject(new Error('jobKey missing'));
      }

      return api.post<ApiResponse<StrategyStatusPayload>>(
        `/api/projects/${projectId}/strategyForYear/status`,
        { jobKey },
      );
    },
    refetchInterval: (query) => {
      const state = (query.state.data as ApiResponse<StrategyStatusPayload> | undefined)?.data?.state;
      return state === 'running' || state === 'queued' ? 2000 : false;
    },
  });

  const submitResponse = submitQuery.data as ApiResponse<StrategyJobResponse> | undefined;
  const statusResponse = statusQuery.data as ApiResponse<StrategyStatusPayload> | undefined;

  const submitJobKey = submitResponse?.data?.jobKey;
  useEffect(() => {
    if (!submitJobKey || submitJobKey === jobKey) {
      return;
    }
    setJobKey(submitJobKey);
  }, [submitJobKey, jobKey]);

  const latestStatus = statusResponse?.data ?? submitResponse?.data?.status ?? null;
  const strategyData = latestStatus?.result || null;
  const loadingState =
    submitQuery.isLoading ||
    submitQuery.isFetching ||
    statusQuery.isLoading ||
    statusQuery.isFetching;
  const error = submitQuery.error || statusQuery.error;

  // Only treat as "processing" once a calculation has actually been requested.
  const isProcessing =
    recalcNonce > 0 &&
    (loadingState ||
      latestStatus?.state === 'running' ||
      latestStatus?.state === 'queued');

  // Out of date only because of edits since the last calculation (strategy/date
  // changes auto-recalculate, so they never leave the result out of date).
  const isOutOfDate = !!selectedStrategy && strategyRefreshToken !== appliedToken;

  const value = useMemo(
    () => ({
      selectedStrategy,
      setSelectedStrategy,
      strategyRefreshToken,
      refreshStrategy,
      strategy: strategyData ? { data: strategyData } : null,
      status: latestStatus,
      isLoading: isProcessing,
      error: error as ApiError | undefined,
      isOutOfDate,
      recalculate,
    }),
    [
      selectedStrategy,
      strategyRefreshToken,
      refreshStrategy,
      strategyData,
      latestStatus,
      isProcessing,
      error,
      isOutOfDate,
      recalculate,
    ],
  );

  return (
    <StrategyContext.Provider value={value}>
      {children}
    </StrategyContext.Provider>
  );
};

export { StrategyContext };
